import React, { useEffect, useState, useContext } from "react";
import axios from "../utils/axios";
import { AuthContext } from "../context/AuthContext";
import "./styles/ReportViewer.css";
import customerlogo from "./assets/ajay.jpeg";

export default function TodayReportViewer() {
  const { user, logout, loading, token } = useContext(AuthContext);
  const [masterSites, setMasterSites] = useState([]);
  const [selectedMasterSiteId, setSelectedMasterSiteId] = useState("");
  const [loadingData, setLoadingData] = useState(false);
  const [reports, setReports] = useState([]);
  const [error, setError] = useState("");

  const authHeader = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const extractDecimal = (value) => {
    if (!value) return 0;
    if (typeof value === "object" && value.$numberDecimal) {
      return parseFloat(value.$numberDecimal);
    }
    return parseFloat(value);
  };

  const fetchMasterSites = async () => {
    try {
      const res = await axios.get("/mastersites", authHeader);
      const filteredSites = user.role === "admin"
        ? res.data
        : res.data.filter(ms => user.masterSiteAccess.includes(ms._id));
      setMasterSites(filteredSites);

      if (filteredSites.length > 0) {
        setSelectedMasterSiteId(filteredSites[0]._id);
      }
    } catch (err) {
      console.error("Error fetching master sites:", err);
      setError("Failed to fetch master sites");
    }
  };

  const fetchTodayReports = async (masterSiteId) => {
    if (!masterSiteId) return;
    setLoadingData(true);
    setError("");
    try {
      const res = await axios.get(`/rep/today/master/${masterSiteId}`, authHeader);
      setReports(res.data);
      if (res.data.length === 0) {
        setError("No data available for today.");
      }
    } catch (err) {
      setError("Failed to fetch today's report");
      console.error(err);
    }
    setLoadingData(false);
  };

  useEffect(() => {
    if (user) {
      fetchMasterSites();
    }
  }, [user]);

  useEffect(() => {
    if (selectedMasterSiteId) {
      fetchTodayReports(selectedMasterSiteId);
    }
  }, [selectedMasterSiteId]);

  const computeTodayTotals = () => {
    let totalWater = 0, totalAmount = 0, cardRechargeAmount = 0, cardSwipeAmount = 0;
    reports.forEach(r => {
      totalWater += extractDecimal(r.totalWaterDispensed);
      totalAmount += extractDecimal(r.totalAmountCollected);
      cardRechargeAmount += extractDecimal(r.cardRechargeAmount || 0);
      cardSwipeAmount += extractDecimal(r.cardSwipeAmount || 0);


    });
    return {
      totalWater: totalWater.toFixed(1),
      totalAmount: totalAmount.toFixed(2),
      cardRechargeAmount: cardRechargeAmount.toFixed(2),
      cardSwipeAmount: cardSwipeAmount.toFixed(2)
    };
  };

  // Figure out if the selected master site is ACCORD_THREE_TAP
  const selectedMasterSite = masterSites.find((ms) => ms._id === selectedMasterSiteId);
  const isThreeTap = selectedMasterSite?.dispenserType === "ACCORD_THREE_TAP";


  // 🔐 Auth handling
  if (loading) return <p>Loading user...</p>;
  if (!user) return <p>🔒 Not logged in</p>;

  return (
    <div className="report-viewer-container">
      <div className="flex justify-between items-center mb-4">
        {isThreeTap && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "8px 12px",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              transition: "all 0.3s ease",
              cursor: "pointer",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#f0f8ff";
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.1)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <img
              src={customerlogo}
              alt="Customer Logo"
              style={{
                width: "45px",
                height: "45px",
                borderRadius: "8px",
                objectFit: "contain",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "15px",
                fontWeight: "500",
                color: "#222",
              }}
            >
              Ajay Infra & Construction
            </p>
          </div>
        )}
        <h2 className="report-heading">Today’s Live Report</h2>
      </div>

      <div className="mb-4">
        <label className="block mb-1 font-medium">Select Master Site:</label>
        <select
          className="border rounded px-2 py-1 w-full"
          value={selectedMasterSiteId}
          onChange={(e) => {
            setSelectedMasterSiteId(e.target.value);
            setReports([]);
            setError("");
          }}
        >
          {masterSites.map((site) => (
            <option key={site._id} value={site._id}>
              {site.name}
            </option>
          ))}
        </select>
      </div>



      {loadingData && <p>Loading...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {!loadingData && !error && reports.length === 0 && (
        <p>No data available for today.</p>
      )}

      {!loadingData && reports.length > 0 && (
        <table className="report-table">
          <thead>
            <tr className="bg-gray-100">
              <th className="border px-2 py-1">Site</th>
              <th className="border px-2 py-1">Water (L)</th>
              <th className="border px-2 py-1">Amount (₹)</th>
              <th className="border px-2 py-1">Card Recharge (₹)</th>
              <th className="border px-2 py-1">Card Swipe (₹)</th>
              <th className="border px-2 py-1">TDS</th>
              <th className="border px-2 py-1">pH</th>

              {isThreeTap && (
                <>
                  <th className="border px-2 py-1">Tap1 (L)</th>
                  <th className="border px-2 py-1">Tap2 (L)</th>
                  <th className="border px-2 py-1">Tap3 (L)</th>
                  <th className="border px-2 py-1">1000 L Tank % Filled</th>
                  <th className="border px-2 py-1">5000 L Tank % Filled</th>
                  <th className="border px-2 py-1">Cold Temp</th>
                  <th className="border px-2 py-1">Normal Temp</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {reports.map((r, index) => (
              <tr key={index}>
                <td className="border px-2 py-1">{r.site}</td>
                <td className="border px-2 py-1">{extractDecimal(r.totalWaterDispensed).toFixed(1)}</td>
                <td className="border px-2 py-1">{extractDecimal(r.totalAmountCollected).toFixed(2)}</td>
                <td className="border px-2 py-1">{extractDecimal(r.cardRechargeAmount).toFixed(2)}</td>
                <td className="border px-2 py-1">{extractDecimal(r.cardSwipeAmount).toFixed(2)}</td>
                <td className="border px-2 py-1">{extractDecimal(r.tds).toFixed(1)}</td>
                <td className="border px-2 py-1">{extractDecimal(r.ph).toFixed(1)}</td>
                {isThreeTap && (
                  <>
                    <td className="border px-2 py-1">{extractDecimal(r.tap1WaterDispensed, 1)}</td>
                    <td className="border px-2 py-1">{extractDecimal(r.tap2WaterDispensed, 1)}</td>
                    <td className="border px-2 py-1">{extractDecimal(r.tap3WaterDispensed, 1)}</td>
                    <td className="border px-2 py-1">{extractDecimal(r.tank1, 0)}</td>
                    <td className="border px-2 py-1">{extractDecimal(r.tank2, 0)}</td>
                    <td className="border px-2 py-1">{extractDecimal(r.temp1, 1)}</td>
                    <td className="border px-2 py-1">{extractDecimal(r.temp2, 1)}</td>
                  </>
                )}
              </tr>
            ))}
            <tr className="bg-yellow-100 font-semibold">
              <td className="border px-2 py-1">Total</td>
              <td className="border px-2 py-1">{computeTodayTotals().totalWater}</td>
              <td className="border px-2 py-1">{computeTodayTotals().totalAmount}</td>
              <td className="border px-2 py-1">{computeTodayTotals().cardRechargeAmount}</td>
              <td className="border px-2 py-1">{computeTodayTotals().cardSwipeAmount}</td>
              <td className="border px-2 py-1">-</td>
              <td className="border px-2 py-1">-</td>

              {isThreeTap && (
                <>
                  <td className="border px-2 py-1">-</td>
                  <td className="border px-2 py-1">-</td>
                  <td className="border px-2 py-1">-</td>
                  <td className="border px-2 py-1">-</td>
                  <td className="border px-2 py-1">-</td>
                  <td className="border px-2 py-1">-</td>
                  <td className="border px-2 py-1">-</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}
